// Kitchen basics (kitchen flow, Stage 2D): what the kitchen should always
// hold. The kitchen asks for everything running low in one tap; the store
// sees the same list and can push a send. Anyone who works the flow (not the
// read-only manager) can choose the basics and their kitchen min / max.
// Strings: basicsStrings.ts. Rules: basicsLogic.ts.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { friendlyError } from '@/lib/inventoryFlow/friendlyError'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { getStoredUserRole } from '@/lib/staff-roles'
import { newIdempotencyKey, queuedFor, type QueuedAction } from '@/lib/inventoryFlow/offlineQueue'
import { intentScope, useIntentKey } from '@/lib/inventoryFlow/intentKey'
import { T } from '@/lib/inventoryFlow/strings'
import { LIVE_POLL_MS, pollInterval, useRequisitionStream } from '@/lib/inventoryFlow/requisitionStream'
import type { Requisition } from '@/lib/inventoryFlow/types'
import type { KitchenRun } from './RequisitionPanel'
import { fmtAmount } from '@/lib/inventoryFlow/planLogic'
import { BT } from '@/lib/inventoryFlow/basicsStrings'
import { canAskBasics, canPushBasics, mergeSends, overStore, sendLines } from '@/lib/inventoryFlow/basicsLogic'
import { QtyStepper } from './QtyStepper'
import { LoadError } from './LoadError'

type Notify = (kind: 'success' | 'error', message: string) => void

function errMessage(e: unknown): string {
  return friendlyError(e, { brief: true })
}

const ASK_PATH = '/inventory-flow/kitchen-basics/request'

export function BasicsTab({ notify, side, run, queue }: {
  notify: Notify
  side: 'kitchen' | 'store'
  /** The kitchen's offline-queue sender (useKitchenActions().run); the store sends directly. */
  run?: KitchenRun
  queue?: QueuedAction[]
}) {
  const role = getStoredUserRole()
  // The store's Basics tab updates live: the kitchen may ask for the same
  // items while the store is about to push them (the kitchen's own screen
  // already listens on the Production page).
  const qc = useQueryClient()
  const refetchBasics = useCallback(() => void qc.invalidateQueries({ queryKey: ['inventory-flow', 'kitchen-basics'] }), [qc])
  const live = useRequisitionStream({ enabled: side === 'store', onEvent: refetchBasics, onReconnect: refetchBasics })
  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['inventory-flow', 'kitchen-basics'],
    queryFn: () => apiClient.getKitchenBasics(),
    refetchInterval: side === 'store' ? pollInterval(live) : LIVE_POLL_MS,
  })
  const rows = useMemo(() => data?.data ?? [], [data])
  const [sends, setSends] = useState<Record<string, number>>({})
  // Amounts the person changed survive a refetch (a live update, a poll);
  // they reset once the list has been sent.
  const edited = useRef<Set<string>>(new Set())
  useEffect(() => setSends((prev) => mergeSends(rows, prev, edited.current)), [rows])
  const low = rows.filter((r) => r.below_min)
  const mayAct = side === 'kitchen' ? canAskBasics(role) : canPushBasics(role)
  const lines = sendLines(sends)
  const over = side === 'store' ? overStore(rows, sends) : []

  const { send, waiting } = useBasicsSend({ side, lines, run, queue, notify, onSent: () => { edited.current = new Set() } })

  return (
    <div className="space-y-5">
      <section className="space-y-3 rounded-lg border p-4">
        <div>
          <h3 className="text-lg font-semibold">{BT.title}</h3>
          <p className="text-sm text-muted-foreground">{BT.help}</p>
        </div>
        {isLoading ? <p className="text-muted-foreground">{T.loading}</p>
          : isError && rows.length === 0 ? <LoadError busy={isFetching} onRetry={() => void refetch()} />
          : rows.length === 0 ? <p className="text-muted-foreground">{BT.none}</p> : (
          <>
            <p className={cn('font-semibold', low.length > 0 && 'text-destructive')}>{BT.lowTitle(low.length)}</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="p-2">{BT.colItem}</th><th className="p-2 text-right">{BT.colKitchen}</th><th className="p-2 text-right">{BT.colMin}</th>
                    <th className="p-2 text-right">{BT.colWay}</th><th className="p-2 text-right">{BT.colStore}</th>{mayAct && <th className="p-2 text-right">{BT.colSend}</th>}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rows.map((r) => (
                    <tr key={r.stock_item_id} className={cn(r.below_min && 'bg-red-50 dark:bg-red-950/30')}>
                      <td className="p-2 font-medium">{r.name}{r.below_min && <span className="ml-2 rounded-full bg-red-600 px-2 py-0.5 text-xs text-white">{BT.low}</span>}</td>
                      <td className="p-2 text-right tabular-nums">{fmtAmount(r.kitchen_on_hand)} {r.unit}</td>
                      <td className="p-2 text-right tabular-nums text-muted-foreground">{fmtAmount(r.min_qty)}{r.max_qty ? `–${fmtAmount(r.max_qty)}` : ''}</td>
                      <td className="p-2 text-right tabular-nums text-muted-foreground">{r.on_the_way > 0 ? fmtAmount(r.on_the_way) : '—'}</td>
                      <td className="p-2 text-right tabular-nums text-muted-foreground">{fmtAmount(r.store_on_hand)}</td>
                      {mayAct && (
                        <td className="p-2 text-right">
                          <QtyStepper className="justify-end" value={sends[r.stock_item_id] ?? 0} unit={r.unit} label={r.name} min={0}
                            onChange={(v) => { edited.current.add(r.stock_item_id); setSends((s) => ({ ...s, [r.stock_item_id]: v })) }} />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {over.length > 0 && <p className="text-sm text-amber-700 dark:text-amber-300">{over.join(', ')}: {BT.colStore} &lt; {BT.colSend}</p>}
            {mayAct && (
              <Button disabled={lines.length === 0 || send.isPending || waiting} onClick={() => send.mutate()}>
                {side === 'kitchen' ? BT.askAll : BT.pushAll}
              </Button>
            )}
            {waiting && <p className="text-sm text-amber-700 dark:text-amber-300">{T.waitingToSend}</p>}
          </>
        )}
      </section>
      {role !== 'manager' && <BasicsSetup notify={notify} />}
    </div>
  )
}

/** Batch 4: the one send behind "Ask for everything low" (kitchen) / "Send
 *  it all" (store), shared with the Today screen's "Ask for them". One key
 *  per send: a retry of the same list reuses it; a changed list or a success
 *  starts a new one. The kitchen's ask goes through the offline queue. */
export function useBasicsSend({ side, lines, run, queue, notify, onSent }: {
  side: 'kitchen' | 'store'
  lines: { stock_item_id: string; qty: number }[]
  run?: KitchenRun
  queue?: QueuedAction[]
  notify: Notify
  /** Sent, or saved to send: the screen's edits are done with. */
  onSent?: () => void
}) {
  const qc = useQueryClient()
  const intent = useIntentKey(intentScope('basics', side, lines))
  const waiting = side === 'kitchen' && !!queue && !!queuedFor(queue, 'POST', ASK_PATH)
  const send = useMutation({
    mutationFn: async (): Promise<{ queued: boolean; req_no: string }> => {
      if (side === 'kitchen' && run) {
        const res = await run<Requisition>(BT.askAll, 'POST', ASK_PATH, { lines }, { key: intent.key, exclusive: true })
        return res.status === 'queued' ? { queued: true, req_no: '' } : { queued: false, req_no: res.data?.req_no ?? '' }
      }
      const res = side === 'kitchen' ? await apiClient.requestKitchenBasics(lines, intent.key) : await apiClient.pushKitchenBasics(lines, intent.key)
      return { queued: false, req_no: res.data?.req_no ?? '' }
    },
    onSuccess: (res) => {
      onSent?.()
      qc.invalidateQueries({ queryKey: ['inventory-flow'] })
      if (res.queued) {
        notify('success', T.queued)
        return
      }
      intent.renew()
      notify('success', side === 'kitchen' ? BT.asked(res.req_no) : BT.pushed(res.req_no))
    },
    onError: (e) => {
      qc.invalidateQueries({ queryKey: ['inventory-flow'] })
      notify('error', errMessage(e))
    },
  })
  return { send, waiting }
}

function BasicsSetup({ notify }: { notify: Notify }) {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [pick, setPick] = useState<{ id: string; name: string; unit: string } | null>(null)
  const [min, setMin] = useState('')
  const [max, setMax] = useState('')
  const { data: itemsRes } = useQuery({ queryKey: ['inventory-flow', 'items'], queryFn: () => apiClient.getFlowItems(), enabled: open, staleTime: 60_000 })
  const { data: basicsRes } = useQuery({ queryKey: ['inventory-flow', 'kitchen-basics'], queryFn: () => apiClient.getKitchenBasics(), enabled: open })
  const basics = basicsRes?.data ?? []
  const matches = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return []
    return (itemsRes?.data ?? []).filter((i) => !i.is_prep && i.name.toLowerCase().includes(q)).slice(0, 15)
  }, [itemsRes, search])
  const save = useMutation({
    mutationFn: (v: { id: string; on: boolean; min: number; max?: number }) =>
      apiClient.setKitchenBasic(v.id, { is_kitchen_basic: v.on, min_qty: v.min, max_qty: v.max ?? null }, newIdempotencyKey()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory-flow', 'kitchen-basics'] })
      notify('success', BT.saved)
      setPick(null)
      setMin('')
      setMax('')
      setSearch('')
    },
    onError: (e) => notify('error', errMessage(e)),
  })
  return (
    <section className="space-y-3 rounded-lg border p-4">
      <button type="button" className="text-base font-semibold underline-offset-4 hover:underline" onClick={() => setOpen((o) => !o)}>{BT.setup}</button>
      {open && (
        <>
          <p className="text-sm text-muted-foreground">{BT.setupHelp}</p>
          {pick ? (
            <div className="flex flex-wrap items-end gap-2">
              <span className="self-center font-medium">{pick.name} ({pick.unit})</span>
              <label className="space-y-1"><span className="text-sm">{BT.min}</span><Input type="number" min={0} className="w-28" value={min} onChange={(e) => setMin(e.target.value)} /></label>
              <label className="space-y-1"><span className="text-sm">{BT.max}</span><Input type="number" min={0} className="w-28" value={max} onChange={(e) => setMax(e.target.value)} /></label>
              <Button disabled={!(Number(min) > 0) || save.isPending}
                onClick={() => save.mutate({ id: pick.id, on: true, min: Number(min), max: max ? Number(max) : undefined })}>{BT.add}</Button>
            </div>
          ) : (
            <>
              <Input placeholder={BT.search} value={search} onChange={(e) => setSearch(e.target.value)} />
              <div className="flex flex-wrap gap-2">
                {matches.map((i) => (
                  <button key={i.id} type="button" className="min-h-10 rounded-full border px-3 text-sm hover:bg-muted" onClick={() => setPick(i)}>{i.name}</button>
                ))}
              </div>
            </>
          )}
          {basics.length > 0 && (
            <ul className="flex flex-wrap gap-2">
              {basics.map((b) => (
                <li key={b.stock_item_id} className="flex items-center gap-2 rounded-full border px-3 py-1 text-sm">
                  {b.name}
                  <button type="button" className="text-muted-foreground underline" onClick={() => save.mutate({ id: b.stock_item_id, on: false, min: 0 })}>{BT.remove}</button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  )
}
